import { useCallback, useEffect, useRef, useState } from 'react';
import { ziniHubWebSocket } from '../config/api';

const HEARTBEAT_MS = 4.5 * 60 * 1000;
const ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

export function useOrderSocket(token, client = 'customer', enabled = true) {
  const socketRef = useRef(null);
  const connectRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const manualCloseRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const [lastMessage, setLastMessage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const isUserActive = useCallback(() => {
    const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    const recentlyActive = Date.now() - lastActivityRef.current < ACTIVE_WINDOW_MS;
    return visible && online && recentlyActive;
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!token || !enabled || !isUserActive()) return;
    if (reconnectTimerRef.current) return;

    const attempt = reconnectAttemptRef.current;
    const baseDelay = Math.min(1000 * (2 ** attempt), MAX_RECONNECT_DELAY_MS);
    const jitter = Math.floor(Math.random() * 500);
    const delay = baseDelay + jitter;

    reconnectAttemptRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectRef.current?.();
    }, delay);
  }, [enabled, isUserActive, token]);

  const connect = useCallback(() => {
    if (!token || !enabled) return;
    if (!isUserActive()) return;

    clearReconnectTimer();

    if (
      socketRef.current &&
      socketRef.current.readyState !== WebSocket.CLOSING &&
      socketRef.current.readyState !== WebSocket.CLOSED
    ) {
      manualCloseRef.current = true;
      socketRef.current.close();
    }

    let wsUrl = ziniHubWebSocket;
    if (typeof wsUrl !== 'string' || !wsUrl.trim()) {
      setError(new Error('REACT_APP_ZINIHUB_WEBSOCKET is not configured'));
      return;
    }
    if (wsUrl.startsWith('http')) wsUrl = wsUrl.replace('http', 'ws');

    try {
      const url = new URL(wsUrl);
      url.searchParams.append('token', token);
      url.searchParams.append('client', client);

      manualCloseRef.current = false;
      const socket = new WebSocket(url.toString());
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        setIsConnected(true);
        setError(null);

        const pingInterval = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: 'ping' }));
          }
        }, HEARTBEAT_MS);

        socket._pingInterval = pingInterval;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;
          setLastMessage({ ...data, _receivedAt: new Date().toISOString() });
        } catch (err) {
          console.error('[OrderWebSocket] Error parsing message:', err);
        }
      };

      socket.onclose = () => {
        setIsConnected(false);
        if (socket._pingInterval) clearInterval(socket._pingInterval);
        const isCurrentSocket = socketRef.current === socket;
        if (isCurrentSocket) socketRef.current = null;

        if (isCurrentSocket && !manualCloseRef.current && enabled && isUserActive()) {
          scheduleReconnect();
        }
      };

      socket.onerror = (err) => {
        setError(err);
        if (socket._pingInterval) clearInterval(socket._pingInterval);
      };
    } catch (err) {
      setError(err);
    }
  }, [clearReconnectTimer, client, enabled, isUserActive, scheduleReconnect, token]);

  connectRef.current = connect;

  useEffect(() => {
    connect();
    return () => {
      clearReconnectTimer();
      manualCloseRef.current = true;
      if (socketRef.current) socketRef.current.close();
    };
  }, [clearReconnectTimer, connect]);

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
      if (enabled && (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED)) {
        reconnectAttemptRef.current = 0;
        connect();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') markActive();
    };

    window.addEventListener('focus', markActive);
    window.addEventListener('online', markActive);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', markActive);
      window.removeEventListener('online', markActive);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [connect, enabled]);

  return { lastMessage, isConnected, error };
}